import React from 'react';
import { useNavigate } from 'react-router-dom';
import { XMarkIcon, SparklesIcon, RocketLaunchIcon, StarIcon } from '@heroicons/react/24/outline';

/**
 * UpgradePrompt Modal Component
 * Shows when user hits subscription limits
 */
export default function UpgradePrompt({ 
  isOpen, 
  onClose, 
  limitType, 
  currentTier, 
  currentUsage, 
  limit,
  suggestedTier = 'premium'
}) {
  const navigate = useNavigate();

  if (!isOpen) return null;

  const limitMessages = {
    recipients: {
      title: "You've reached your recipient limit",
      description: `You've added ${currentUsage} of ${limit} recipients on the ${currentTier} plan.`,
      icon: '👥',
      benefit: 'Add unlimited recipients and never worry about limits again'
    },
    messages: {
      title: "You've reached your message limit",
      description: `You've sent ${currentUsage} of ${limit} messages this month on the ${currentTier} plan.`,
      icon: '💬',
      benefit: 'Send unlimited messages and share your voice without restrictions'
    },
    whatsapp: {
      title: "You've reached your WhatsApp delivery limit",
      description: `You've sent ${currentUsage} of ${limit} WhatsApp messages this month on the ${currentTier} plan.`,
      icon: '📱',
      benefit: 'Unlimited WhatsApp delivery to stay connected with loved ones'
    },
    podcast_sessions: {
      title: "You've reached your podcast session limit",
      description: `You've used ${currentUsage} of ${limit} podcast sessions this month on the ${currentTier} plan.`,
      icon: '🎙️',
      benefit: 'Unlimited podcast sessions to preserve your stories and wisdom'
    }
  };

  const message = limitMessages[limitType] || limitMessages.messages;

  const tierRecommendations = {
    free: {
      tier: 'Starter',
      price: '$14.99/month',
      features: [
        '15 voice messages per month',
        '5 recipients',
        'Full voice cloning',
        '1 podcast session per month'
      ],
      color: 'from-blue-600 to-indigo-600'
    },
    starter: {
      tier: 'Premium',
      price: '$39.99/month',
      features: [
        'UNLIMITED messages',
        'UNLIMITED recipients',
        'UNLIMITED podcast sessions',
        'AI chatbot conversations',
        'Priority support'
      ],
      color: 'from-purple-600 to-pink-600'
    }
  };

  const recommendation = tierRecommendations[currentTier?.toLowerCase()] || tierRecommendations.starter;

  const handleUpgrade = () => {
    navigate('/pricing');
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 animate-fadeIn">
      <div className="bg-white rounded-2xl max-w-lg w-full shadow-2xl animate-slideUp">
        {/* Header */}
        <div className="relative bg-gradient-to-r from-purple-50 to-pink-50 p-6 rounded-t-2xl border-b border-purple-100">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
          
          <div className="flex items-center space-x-3 mb-2">
            <div className="text-4xl">{message.icon}</div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">{message.title}</h2>
            </div>
          </div>
          <p className="text-gray-700 mt-2">{message.description}</p>
        </div>

        {/* Body */}
        <div className="p-6">
          {/* Benefit Highlight */}
          <div className="bg-gradient-to-r from-purple-100 to-pink-100 rounded-xl p-4 mb-6">
            <div className="flex items-start space-x-3">
              <SparklesIcon className="h-6 w-6 text-purple-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Unlock More with {recommendation.tier}</h3>
                <p className="text-sm text-gray-700">{message.benefit}</p>
              </div>
            </div>
          </div>

          {/* Recommended Plan */}
          <div className="border-2 border-purple-200 rounded-xl p-5 mb-6 bg-white">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="flex items-center space-x-2">
                  <h3 className="text-xl font-bold text-gray-900">{recommendation.tier} Plan</h3>
                  <StarIcon className="h-5 w-5 text-yellow-500 fill-current" />
                </div>
                <p className="text-2xl font-extrabold text-purple-600 mt-1">{recommendation.price}</p>
              </div>
              <RocketLaunchIcon className="h-12 w-12 text-purple-600" />
            </div>

            <ul className="space-y-2">
              {recommendation.features.map((feature, idx) => (
                <li key={idx} className="flex items-start text-sm">
                  <span className="text-green-500 mr-2 flex-shrink-0">✓</span>
                  <span className="text-gray-700">{feature}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Actions */}
          <div className="space-y-3">
            <button
              onClick={handleUpgrade}
              className={`w-full py-4 px-6 bg-gradient-to-r ${recommendation.color} text-white font-semibold rounded-xl hover:shadow-lg transition-all transform hover:scale-105`}
            >
              Upgrade to {recommendation.tier}
            </button>
            <button
              onClick={onClose}
              className="w-full py-3 px-6 text-gray-600 font-medium hover:text-gray-800 transition-colors"
            >
              Maybe Later
            </button>
          </div>

          {/* Trust Badge */}
          <div className="mt-6 text-center">
            <p className="text-xs text-gray-500">
              ✓ Cancel anytime • ✓ Instant upgrade • ✓ No hidden fees
            </p>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { 
            opacity: 0;
            transform: translateY(20px);
          }
          to { 
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out;
        }
        .animate-slideUp {
          animation: slideUp 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}
